import { useState } from "react";
import { toast } from "sonner";
import { FileText, Send, Trash2, Pencil, Loader2, Eye, Download, Wand2, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, EmptyState } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { ClientAvatar } from "@/components/client/ClientAvatar";
import { openBlob } from "@/lib/invoices";

export const notifyDraftsChanged = () => window.dispatchEvent(new Event("core-drafts-changed"));
const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;

const EditDialog = ({ draft, onClose, onSaved }) => {
  const [f, setF] = useState(() => ({ nota: draft.nota, to: draft.to.join(", "), oggetto: draft.oggetto, messaggio: draft.messaggio }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/report-bozze/${draft.id}`, { ...f, to: f.to.split(/[,;\s]+/).filter(Boolean) });
      toast.success(f.nota !== draft.nota ? "Bozza salvata e PDF rigenerato" : "Bozza salvata"); onSaved(r.data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="draft-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{draft.cliente} · {draft.mese_label}</DialogTitle>
          <DialogDescription>Modificando la nota il PDF viene rigenerato. Nulla viene inviato finché non premi «Invia».</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><L>Nota dell'agenzia (nel PDF)</L><Textarea rows={4} value={f.nota} onChange={set("nota")} className="rounded-xl" data-testid="draft-nota" /></div>
          <div><L>Destinatari</L><Input value={f.to} onChange={set("to")} placeholder="email@cliente.it, altra@cliente.it" className="rounded-xl" data-testid="draft-to" /></div>
          <div><L>Oggetto</L><Input value={f.oggetto} onChange={set("oggetto")} className="rounded-xl" data-testid="draft-oggetto" /></div>
          <div><L>Messaggio</L><Textarea rows={6} value={f.messaggio} onChange={set("messaggio")} className="rounded-xl" data-testid="draft-messaggio" /></div>
        </div>
        <DialogFooter><Button variant="ghost" className="rounded-full" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !f.oggetto.trim()} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="draft-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Salva bozza</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GenerateBox = ({ onDone }) => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [mese, setMese] = useState(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/report-bozze/genera", { mese }); toast.success(`${r.data.bozze_create || 0} bozze create · ${r.data.saltati} saltate (nessuna attività) · ${r.data.duplicati} già presenti`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2" data-testid="draft-generate-box">
      <Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="rounded-full w-44 bg-white" data-testid="draft-generate-month" />
      <Button onClick={run} disabled={busy || !mese} variant="outline" className="rounded-full font-bold" data-testid="draft-generate-run">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Wand2 className="h-4 w-4 mr-1.5" />}Genera ora</Button>
    </div>
  );
};

export default function ReportDraftsPage() {
  const { user } = useAuth();
  const [stato, setStato] = useState("da_controllare");
  const { data, reload, setData } = useFetch(`/report-bozze?stato=${stato}`);
  const [edit, setEdit] = useState(null);
  const [discard, setDiscard] = useState(null);
  const [sending, setSending] = useState(null);
  const refresh = () => { reload(); notifyDraftsChanged(); };
  const pdf = async (d, download) => {
    try { const r = await api.get(`/report-bozze/${d.id}/pdf`, { responseType: "blob" }); openBlob(r.data, d.filename, download); }
    catch { toast.error("PDF non disponibile"); }
  };
  const send = async (d) => {
    if (!d.to.length) return toast.error("Aggiungi almeno un destinatario");
    setSending(d.id);
    try { await api.post(`/report-bozze/${d.id}/invia`); toast.success(`Report inviato a ${d.to.join(", ")}`); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSending(null); }
  };
  const doDiscard = async () => {
    try { await api.post(`/report-bozze/${discard.id}/scarta`); toast.success("Bozza scartata"); refresh(); } catch (e) { toast.error(errMsg(e)); }
    setDiscard(null);
  };
  const open = stato === "da_controllare";
  return (
    <div data-testid="report-drafts-page">
      <PageHeader eyebrow="Report mensili" title="Report da inviare"
        subtitle="Il 1° di ogni mese alle 08:00 Core prepara in bozza i report del mese precedente. Controllali e inviali tu: ai clienti non parte nulla in automatico."
        actions={user.role === "admin" && <GenerateBox onDone={refresh} />} />
      <Tabs value={stato} onValueChange={setStato} className="mb-5">
        <TabsList className="bg-white/60 rounded-full p-1">
          {[["da_controllare", "Da controllare"], ["inviato", "Inviati"], ["scartato", "Scartati"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-full data-[state=active]:bg-[#0A1128] data-[state=active]:text-white font-bold" data-testid={`drafts-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {data && !data.length && <Panel><EmptyState icon={Inbox} title={open ? "Nessun report da controllare" : "Nessun report"} text={open ? "Le nuove bozze arrivano il 1° del mese." : ""} testid="drafts-empty" /></Panel>}
      <div className="space-y-3">
        {(data || []).map((d) => (
          <Panel key={d.id} className="p-5 flex items-center gap-5" data-testid={`draft-row-${d.id}`}>
            <ClientAvatar client={d} testid={`draft-avatar-${d.id}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><span className="font-bold text-[#0A1128]">{d.cliente}</span>
                <span className="text-[11px] font-bold rounded-full bg-[#A6DAFC] px-2 py-0.5 text-[#0A1128]">{d.mese_label}</span>
                {open && <span className="text-[11px] font-bold rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800">Da controllare</span>}</div>
              <div className="text-xs text-slate-600 mt-0.5 truncate">{d.oggetto} · A: {d.to.join(", ") || <span className="text-[#C8102E] font-bold">nessun destinatario</span>}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{d.attivita} elementi nel mese{d.nota ? " · con nota" : ""}{d.sent_at ? ` · inviato il ${fmtDateTime(d.sent_at)} da ${d.sent_by_nome}` : ""}{d.error ? ` · ultimo errore: ${d.error}` : ""}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => pdf(d, false)} className="p-2 rounded-lg hover:bg-[#8FD0FB]" title="Anteprima PDF" data-testid={`draft-preview-${d.id}`}><Eye className="h-4 w-4" /></button>
              <button onClick={() => pdf(d, true)} className="p-2 rounded-lg hover:bg-[#8FD0FB]" title="Scarica PDF" data-testid={`draft-download-${d.id}`}><Download className="h-4 w-4" /></button>
              {open && <>
                <button onClick={() => setEdit(d)} className="p-2 rounded-lg hover:bg-[#8FD0FB]" title="Modifica" data-testid={`draft-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setDiscard(d)} className="p-2 rounded-lg hover:bg-red-50 text-[#E8173A]" title="Scarta" data-testid={`draft-discard-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                <Button onClick={() => send(d)} disabled={sending === d.id} className="ml-2 rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid={`draft-send-${d.id}`}>
                  {sending === d.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Invia</Button>
              </>}
              {!open && <FileText className="h-4 w-4 text-slate-400 ml-2" />}
            </div>
          </Panel>
        ))}
      </div>
      {edit && <EditDialog draft={edit} onClose={() => setEdit(null)} onSaved={(n) => setData((xs) => xs.map((x) => (x.id === n.id ? n : x)))} />}
      <ConfirmDialog open={!!discard} onOpenChange={(o) => !o && setDiscard(null)} title="Scartare la bozza?" confirmLabel="Scarta"
        description={`Il report di ${discard?.mese_label} per ${discard?.cliente} non verrà inviato.`} onConfirm={doDiscard} />
    </div>
  );
}
